/* eslint-disable camelcase */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { BsCreditCard } from 'react-icons/bs';
import { FormHandles } from '@unform/core';
import * as Yup from 'yup';
import valid from 'card-validator';
import { AnimatedContainer, ButtonLoadingForm } from '../styles';
import { StepIndicator } from '../components/StepIndicator';
import { ComponentUserRegisterProps, stepLabels } from '..';
import {
  AddressCheckBoxContainer,
  ChangeMethodContainer,
  ContainerForm,
  LineInputOne,
  LineInputThree,
  LineInputTwo,
  PaymentMethodContainer,
  PlanInfoContainer,
  PlanPriceContainer,
  PriceContainer,
  SelectedPaymentMethod,
  SelectedPlanContainer,
} from './styles';
import Input from '../../../components/Input';
import InputMask from '../../../components/InputMask';
import Checkbox from '../../../components/Checkbox';
import getValidationCPF from '../../../utils/getValidationCPF';
import getValidationErrors from '../../../utils/getValidationErrors';
import InputCep from '../../../components/InputCep';
import InputSelect from '../../../components/InputSelect';
import getUF from '../../../utils/getUF';
import { useCep } from '../../../hooks/cep';
import { PaymentMethodEnum } from '../../../enums/PaymentMethodEnum';
import { listPlanService } from '../../../services/listPlanService';
import { startSubscriptionPaymentService } from '../../../services/startSubscriptionPaymentService';
import { useUser } from '../../../hooks/user';
import { createPatientService } from '../../../services/createPatientService';
import { UserTypeEnum } from '../../../enums/UserTypeEnum';

interface formProps {
  name: string;
  number: string;
  cvv: string;
  dueDate: string;
  cpf: string;
  myAddress?: boolean;
  cep: string;
  street: string;
  streetNumber: string;
  complement: string;
  city: string;
  uf: string;
}

export const CreditCardPayment: React.FC<ComponentUserRegisterProps> = ({
  handleScreenSelection,
  handlePaymentError,
  nextPage,
}) => {
  const formRef = useRef<FormHandles>(null);
  const [btnIsLoading, setBtnIsLoading] = useState<boolean>(false);

  const [shouldUpdateInputs, setShouldUpdateInputs] = useState(false);

  const { getAddressData, addressData, loading, error } = useCep();

  const { user } = useUser();

  useEffect(() => {
    if (error) {
      formRef.current?.setFieldError('cep', error);
    }
  }, [error]);

  useEffect(() => {
    if (addressData && shouldUpdateInputs) {
      formRef.current?.setFieldValue('street', addressData.street);
      formRef.current?.setFieldValue('complement', addressData.complement);
      formRef.current?.setFieldValue('neighborhood', addressData.neighborhood);
      formRef.current?.setFieldValue('city', addressData.city);
      formRef.current?.setFieldValue('uf', addressData.uf);
      formRef.current?.setFieldError('cep', '');
    }
  }, [addressData, shouldUpdateInputs]);

  const handleNext = useCallback(async () => {
    const data = formRef?.current?.getData() as formProps;

    try {
      setBtnIsLoading(true);

      formRef.current?.setErrors({});

      const schema = Yup.object().shape({
        name: Yup.string()
          .required('O nome do titular do cartão é obrigatório')
          .test({
            name: 'fullNameTest',
            test: value => !!value && value.trim().split(' ').length > 1,
            message: 'O nome completo é obrigatório',
          }),
        number: Yup.string()
          .required('O número do cartão é obrigatório')
          .test({
            name: 'cardNumberValidation',
            test: number => {
              if (number) {
                const { isValid, card: cardInfo } = valid.number(number);

                if (!isValid || !cardInfo) {
                  return false;
                }
              }

              return true;
            },
            message: 'Número do cartão inválido',
          }),
        cvv: Yup.string()
          .length(3, 'Digite um número válido')
          .required('O cvv do cartão de crédito é obrigatório'),
        dueDate: Yup.string()
          .required('A validade do cartão é obrigatória (MM/AA)')
          .test({
            name: 'validMonth',
            test: expiration => {
              if (expiration && expiration.length >= 2) {
                const [monthStr] = expiration.split('/');
                const monthNum = Number(monthStr);

                if (monthNum < 1 || monthNum > 12) {
                  return false;
                }
              }

              return true;
            },
            message: 'Mês informado inválido',
          })
          .length(5, 'A validade do cartão é obrigatória (MM/AA)')
          .test({
            name: 'validExpiration',
            test: expiration => {
              if (expiration) {
                const { isValid } = valid.expirationDate(expiration);

                if (!isValid) {
                  return false;
                }
              }
              return true;
            },
            message: 'No mínimo nesse mês e no máximo em 19 anos',
          }),
        cpf: Yup.string()
          .required('O CPF é obrigatório')
          .length(14, 'Digite um CPF válido')
          .test('cpf', 'Digite um CPF válido', value =>
            getValidationCPF(value),
          ),
        cep: Yup.string()
          .test(
            'length',
            'Digite um CEP válido',
            value => !!(!value || value.length === 9),
          )
          .required('O cep é obrigatório'),
        street: Yup.string().required('A rua é obrigatório'),
        // neighborhood: Yup.string().required('O bairro é obrigatório'),
        streetNumber: Yup.string().required('O número da rua é obrigatório'),
        complement: Yup.string(),
        city: Yup.string().required('A cidade é obrigatória'),
        uf: Yup.string().required('O estado é obrigatório'),
      });

      await schema.validate(data, {
        abortEarly: false,
      });

      const exp_month = Number(data.dueDate.split('/')[0]);
      const exp_year = Number(data.dueDate.split('/')[1]);

      const { plans } = (await listPlanService({})) || {};

      const requestedUser = await createPatientService({
        personalData: {
          name: user?.name || '',
          cpf: data.cpf,
          email: user?.email,
          phone: user?.phone,
          birthday: user?.birthDate,
          profileType: 'patient',
        },
        patientData: {},
        type: UserTypeEnum.PATIENT,
      });

      if (requestedUser) {
        await startSubscriptionPaymentService({
          userId: requestedUser.id ?? '',
          planId: plans[0].id,
          paymentMethod: PaymentMethodEnum.CreditCard,
          pagarmeData: {
            customer: {
              name: user?.name ?? '',
              email: user?.email ?? '',
            },
            payment_method: PaymentMethodEnum.CreditCard,
            card: {
              billing_address: {
                line_1: `${addressData?.street}, ${addressData?.neighborhood}`,
                city: addressData?.city || '',
                state: addressData?.uf || '',
                country: 'BR',
                zip_code: addressData?.cep.replace(/-/g, '') || '',
              },
              holder_name: user?.name || '',
              number: data.number.replace(/\s+/g, ''),
              cvv: data.cvv,
              exp_month,
              exp_year,
            },
          },
        });
      } else {
        handlePaymentError();
      }

      nextPage();
    } catch (err) {
      setBtnIsLoading(false);
      if (err instanceof Yup.ValidationError) {
        const errors = getValidationErrors(err);
        formRef.current?.setErrors(errors);
      }
    }
  }, [nextPage, handlePaymentError, addressData, user]);

  const handleInputCep = useCallback(
    async value => {
      if (value[value.length - 1] !== '_') {
        await getAddressData(formRef.current?.getFieldValue('cep'));
        setShouldUpdateInputs(true);
      }
    },
    [getAddressData],
  );

  const handleAddressCheckBoxClick = useCallback(() => {
    const value = shouldUpdateInputs;

    if (value) {
      formRef.current?.reset();
    }

    setShouldUpdateInputs(!value);
  }, [shouldUpdateInputs]);

  return (
    <AnimatedContainer>
      <StepIndicator activeIndex={3} titles={stepLabels} />
      <h1>Pagar com Cartão</h1>

      <SelectedPlanContainer>
        <PlanInfoContainer>
          <span>Assinatura</span>
          <span className="timeFrame">Assistente Virtual</span>
        </PlanInfoContainer>
        <PlanPriceContainer>
          <PriceContainer>
            <span>
              R$ 290,00 <span className="smallText">no Cartão</span>
            </span>
          </PriceContainer>
          <span>Descrição:</span>
          <span className="highlightedText">Assinatura mensal</span>
          <span className="highlightedText">R$ 40,00</span>
          <span>+ R$ 250,00</span>
          <span>Pulseira inteligente</span>
        </PlanPriceContainer>
      </SelectedPlanContainer>
      <PaymentMethodContainer>
        <SelectedPaymentMethod>
          <BsCreditCard size={22} />
          <span>Cartão de crédito</span>
        </SelectedPaymentMethod>
        <ChangeMethodContainer
          onClick={() => handleScreenSelection(PaymentMethodEnum.Pix)}
        >
          <span>ALTERAR</span>
        </ChangeMethodContainer>
      </PaymentMethodContainer>

      <ContainerForm ref={formRef} onSubmit={handleNext}>
        <Input name="name" label="Nome do titular" />
        <InputMask
          name="number"
          label="Número do Cartão"
          isRequired
          mask="9999 9999 9999 9999"
        />
        <LineInputOne>
          <InputMask name="cvv" label="CVV" isRequired mask="999" />

          <InputMask
            name="dueDate"
            label="Vencimento"
            isRequired
            mask="99/99"
          />
        </LineInputOne>

        <InputMask
          name="cpf"
          label="CPF/CNPJ do titular"
          isRequired
          type="tel"
          mask="999.999.999-99"
        />

        <AddressCheckBoxContainer>
          <Checkbox name="myAddress" onClick={handleAddressCheckBoxClick} />
          <span>Mesmo endereço de cobrança</span>
        </AddressCheckBoxContainer>
        <InputCep
          isLoading={loading}
          mask="99999-999"
          onChange={e => handleInputCep(e.target.value)}
          name="cep"
          label="CEP"
          type="tel"
        />
        <Input name="street" label="Rua" />

        <LineInputTwo>
          <Input name="streetNumber" label="Número" type="number" />

          <Input name="complement" label="Complemento" />
        </LineInputTwo>

        <LineInputThree>
          <Input name="city" label="Cidade" />

          <InputSelect
            name="uf"
            label="Estado"
            options={getUF}
            placeholder="Selecione"
          />
        </LineInputThree>
      </ContainerForm>

      <ButtonLoadingForm
        isLoading={btnIsLoading}
        btnType="darkBlue"
        onClick={handleNext}
      >
        Concluir
      </ButtonLoadingForm>
    </AnimatedContainer>
  );
};
